import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { ClipboardList, CheckCircle2, Circle, Upload, FileText, ChevronRight, AlertTriangle, Shield } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useParams } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_ICON = {
  GREEN: <CheckCircle2 className="w-5 h-5 text-emerald-400" />,
  AMBER: <AlertTriangle className="w-5 h-5 text-amber-400" />,
  RED: <Circle className="w-5 h-5 text-red-400" />,
  GREY: <Circle className="w-5 h-5 text-zinc-400" />,
};

export default function StaffOnboarding() {
  const { staffId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadDialog, setUploadDialog] = useState(null);
  const [fileUrl, setFileUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  const fetchChecklist = useCallback(async () => {
    if (!staffId) return;
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/api/hr-compliance/onboarding/${staffId}`, { withCredentials: true });
      setData(res);
    } catch { toast.error('Failed to load onboarding checklist'); }
    setLoading(false);
  }, [staffId]);

  useEffect(() => { fetchChecklist(); }, [fetchChecklist]);

  const completeStep = async (reqId, critical) => {
    try {
      await axios.post(`${API}/api/hr-compliance/onboarding/${staffId}/complete-step`, {
        requirement_id: reqId, deployment_critical: critical, evidence_status: 'UPLOADED'
      }, { withCredentials: true });
      toast.success('Step completed');
      fetchChecklist();
    } catch { toast.error('Failed to complete step'); }
  };

  const uploadEvidence = async () => {
    if (!fileUrl.trim() || !uploadDialog) return;
    setUploading(true);
    try {
      await axios.post(`${API}/api/hr-compliance/staff/${staffId}/evidence-upload`, {
        requirement_id: uploadDialog.requirement_id, file_url: fileUrl,
        document_type: uploadDialog.name, expiry_at: null,
      }, { withCredentials: true });
      toast.success('Evidence uploaded');
      setUploadDialog(null);
      setFileUrl('');
      fetchChecklist();
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  if (loading) return <DashboardLayout><div className="text-center py-12"><p className="text-muted-foreground">Loading...</p></div></DashboardLayout>;
  if (!data) return <DashboardLayout><div className="text-center py-12"><p className="text-muted-foreground">Staff not found</p></div></DashboardLayout>;

  const groupedSteps = {};
  data.steps.forEach(s => {
    if (!groupedSteps[s.category]) groupedSteps[s.category] = [];
    groupedSteps[s.category].push(s);
  });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="onboarding-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
              <ClipboardList className="w-6 h-6 text-[#00A6A0]" />Staff Onboarding
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{data.staff_name}</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-[#00A6A0]">{data.progress_pct}%</p>
            <p className="text-xs text-muted-foreground">{data.completed}/{data.total} complete</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-muted rounded-full h-3 overflow-hidden" data-testid="progress-bar">
          <div className="h-full bg-[#00A6A0] rounded-full transition-all duration-500" style={{ width: `${data.progress_pct}%` }} />
        </div>

        {/* Steps by category */}
        {Object.entries(groupedSteps).map(([cat, steps]) => (
          <Card key={cat} className="glass-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#00A6A0]" />{cat}
                <span className="text-[10px] text-muted-foreground ml-auto">{steps.filter(s => s.completed).length}/{steps.length}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {steps.map(step => (
                <div key={step.requirement_id} className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${step.completed ? 'bg-emerald-500/5' : 'bg-muted/30'}`}
                  data-testid={`step-${step.requirement_id}`}>
                  {STATUS_ICON[step.status]}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm ${step.completed ? 'text-muted-foreground line-through' : 'text-foreground font-medium'}`}>{step.name}</span>
                      {step.deployment_critical && <span className="text-[10px] px-1 py-0.5 rounded bg-red-500/10 text-red-400">CRITICAL</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-muted-foreground">Evidence: {step.evidence_status}</span>
                      {step.forms.length > 0 && (
                        <span className="text-[10px] text-[#00A6A0]">
                          <FileText className="w-3 h-3 inline mr-0.5" />{step.forms[0].name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {!step.completed && (
                      <>
                        <Button size="sm" variant="outline" className="text-[10px] h-7 px-2"
                          onClick={() => setUploadDialog(step)} data-testid={`upload-${step.requirement_id}`}>
                          <Upload className="w-3 h-3 mr-1" />Evidence
                        </Button>
                        <Button size="sm" className="text-[10px] h-7 px-2 bg-[#00A6A0] hover:bg-[#00A6A0]/80 text-white"
                          onClick={() => completeStep(step.requirement_id, step.deployment_critical)} data-testid={`complete-${step.requirement_id}`}>
                          <CheckCircle2 className="w-3 h-3 mr-1" />Done
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Evidence Upload Dialog */}
      <Dialog open={!!uploadDialog} onOpenChange={() => setUploadDialog(null)}>
        <DialogContent className="glass-card border-border">
          <DialogHeader>
            <DialogTitle>Upload Evidence: {uploadDialog?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Provide the URL of the uploaded evidence file (DBS certificate, training cert, etc.)</p>
            <input value={fileUrl} onChange={e => setFileUrl(e.target.value)}
              placeholder="https://storage.example.com/evidence/document.pdf"
              className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm"
              data-testid="evidence-url-input" />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setUploadDialog(null)}>Cancel</Button>
              <Button className="bg-[#00A6A0] text-white" onClick={uploadEvidence} disabled={uploading || !fileUrl.trim()} data-testid="submit-evidence-btn">
                {uploading ? 'Uploading...' : 'Upload Evidence'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
